// src/contexts/DataContext.js
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet-gpx';

export const DataContext = createContext();

export function DataProvider({ children }) {
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);

  const [trackpoints, setTrackpoints] = useState([]);
  const [latest, setLatest] = useState(null);
  const [stats, setStats] = useState(null);
  const [gpxPoints, setGpxPoints] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    function connectWebSocket() {
      if (
        ws.current &&
        (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)
      ) return;

      console.log('🔁 Attempting WebSocket connection...');
      ws.current = new WebSocket('wss://nm.digitalestudent.nl:8443');

      ws.current.onopen = () => {
        console.log('✅ WebSocket connected');
        setIsConnected(true);
        ws.current.send(JSON.stringify({ type: 'reader' }));
      };

      ws.current.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === 'history' && Array.isArray(msg.data)) {
            const cleaned = msg.data.filter(isValidPoint);
            setTrackpoints(cleaned);

            if (cleaned.length > 0) {
              const last = cleaned[cleaned.length - 1];
              setLatest(last);
              setStats(calculateAvgSpeed10(cleaned));
            }

            console.log(`📜 Loaded ${cleaned.length} historical trackpoints`);
          }

          else if (msg.type === 'trackpoint' && isValidPoint(msg.data)) {
            setTrackpoints((prev) => {
              const updated = [...prev, msg.data];
              setLatest(msg.data);
              setStats(calculateAvgSpeed10(updated));
              return updated;
            });
          }

         else if (msg.type === 'update' && msg.data?.latest && isValidPoint(msg.data.latest)) {
          setTrackpoints((prev) => {
            const last = prev[prev.length - 1];
            const isDuplicate =
              last &&
              last.timestampMs === msg.data.latest.timestampMs &&
              last.lat === msg.data.latest.lat &&
              last.lon === msg.data.latest.lon;

            const updated = isDuplicate ? prev : [...prev, msg.data.latest];

            setLatest(msg.data.latest);
            setStats(calculateAvgSpeed10(updated));
            return updated;
          });
        }


        } catch (err) {
          console.error('⚠️ WebSocket message error:', err);
        }
      };

      ws.current.onerror = (err) => {
        console.error('❌ WebSocket error:', err);
      };

      ws.current.onclose = () => {
        console.warn('🔌 WebSocket disconnected');
        setIsConnected(false);

        if (!reconnectTimeout.current) {
          reconnectTimeout.current = setTimeout(() => {
            reconnectTimeout.current = null;
            connectWebSocket();
          }, 3000); // Retry after 3 seconds
        }
      };
    }

    connectWebSocket();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        console.log('👀 Tab visible — attempting reconnect');
        connectWebSocket();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      ws.current?.close();
      clearTimeout(reconnectTimeout.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  useEffect(() => {
    fetch('/route.gpx')
      .then((res) => res.text())
      .then((gpxText) => {
        const parser = new DOMParser();
        const gpxDoc = parser.parseFromString(gpxText, 'application/xml');
        const trkpts = gpxDoc.querySelectorAll('trkpt');

        const points = Array.from(trkpts).map((pt) => {
          const lat = parseFloat(pt.getAttribute('lat'));
          const lon = parseFloat(pt.getAttribute('lon'));
          return L.latLng(lat, lon);
        });

        if (!points.length) throw new Error('No trackpoints found');
        console.log('📍 Loaded GPX points:', points.length);
        setGpxPoints(points);
      })
      .catch((err) => {
        console.error('❌ Failed to load GPX:', err);
      });
  }, []);

  useEffect(() => {
    fetch('../checkpoints.json')
      .then((res) => res.json())
      .then(setCheckpoints)
      .catch((err) => console.error('❌ Failed to load checkpoints:', err));
  }, []);

  return (
    <DataContext.Provider
      value={{
        trackpoints,
        latest,
        stats,
        gpxPoints,
        checkpoints,
        isConnected
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

function isValidPoint(data) {
  return (
    typeof data === 'object' &&
    !isNaN(parseFloat(data.lat)) &&
    !isNaN(parseFloat(data.lon))
  );
}

function calculateAvgSpeed10(trackpoints) {
  const now = Date.now();
  const recent = trackpoints.filter(
    (p) => p.timestampMs && now - p.timestampMs <= 10 * 60 * 1000
  );

  const speeds = recent.map((p) => p.speedKmh).filter((v) => !isNaN(v));
  const avg = speeds.length
    ? parseFloat((speeds.reduce((a, b) => a + b, 0) / speeds.length).toFixed(2))
    : 0;

  return { avgSpeed10: avg };
}

export function useData() {
  return useContext(DataContext);
}
